import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import { Play, Receipt, AlertCircle, DollarSign, CheckCircle2 } from 'lucide-react';
import { PageHeader, Card, StatTile, Badge, EmptyState, DataTable, Toolbar } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';

const statusTone = s => ({
    paid: 'success', sent: 'primary', overdue: 'danger', draft: 'neutral', cancelled: 'neutral',
}[s] || 'neutral');

const Invoices = () => {
    const { user } = useAuth();
    const canGenerate = user?.user_level === 'admin';
    const [invoices, setInvoices] = useState([]);
    const [arrears, setArrears] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [search, setSearch] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const [i, a] = await Promise.all([
                axios.get('/api/invoices'),
                axios.get('/api/ledger/arrears').catch(() => ({ data: { rows: [] } })),
            ]);
            setInvoices(i.data.invoices || []);
            setArrears(a.data.rows || []);
        } catch { toast.error('Failed to load'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const generate = async () => {
        const t = toast.loading('Generating…');
        try { const { data } = await axios.post('/api/ledger/jobs/generate-invoices');
              toast.success(`Created ${data.created}`, { id: t }); load(); }
        catch (e) { toast.error(e.response?.data?.message || 'Failed', { id: t }); }
    };

    const totalOutstanding = invoices.reduce((s,i) => s + Number(i.balance_due || 0), 0);
    const totalOverdue = invoices.filter(i => i.status === 'overdue').reduce((s,i) => s + Number(i.balance_due || 0), 0);
    const totalCollected = invoices.reduce((s,i) => s + Number(i.paid_amount || 0), 0);

    const filtered = invoices.filter(i => {
        if (!search) return true;
        const q = search.toLowerCase();
        return i.invoice_number?.toLowerCase().includes(q)
            || i.tenant_name?.toLowerCase().includes(q)
            || i.property_name?.toLowerCase().includes(q);
    });

    return (
        <div className="animate-fade-in">
            <PageHeader
                title="Invoices"
                subtitle="Monthly rent invoices and their payment status."
                actions={
                    canGenerate && <button onClick={generate} className="btn-primary">
                        <Play className="h-4 w-4" /> Generate this month
                    </button>
                }
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
                <StatTile label="Total invoices"  value={invoices.length} icon={Receipt} accent="primary" />
                <StatTile label="Collected"       value={formatCurrency(totalCollected)} icon={CheckCircle2} accent="success" />
                <StatTile label="Outstanding"     value={formatCurrency(totalOutstanding)} icon={DollarSign} accent="warning" />
                <StatTile label="Overdue"         value={formatCurrency(totalOverdue)}     icon={AlertCircle} accent="danger" />
            </div>

            {arrears.length > 0 && (
                <Card title="Arrears aging" subtitle="Overdue balances bucketed by days late" padding={false} className="mb-6">
                    <DataTable
                        density="compact"
                        columns={[
                            { key: 'tenant_name', label: 'Tenant',
                              render: r => <span className="font-medium text-neutral-900">{r.tenant_name}</span> },
                            { key: 'property_name', label: 'Property' },
                            { key: 'current',  label: 'Current', align: 'right', render: r => formatCurrency(r.current) },
                            { key: 'd1_30',    label: '1–30',    align: 'right', render: r => formatCurrency(r.d1_30) },
                            { key: 'd31_60',   label: '31–60',   align: 'right', render: r => formatCurrency(r.d31_60) },
                            { key: 'd61_90',   label: '61–90',   align: 'right', render: r => formatCurrency(r.d61_90) },
                            { key: 'd90_plus', label: '90+',     align: 'right',
                              render: r => Number(r.d90_plus) > 0 ? <span className="text-danger-700 font-medium">{formatCurrency(r.d90_plus)}</span> : '—' },
                            { key: 'total_outstanding', label: 'Total', align: 'right',
                              render: r => <span className="font-semibold text-neutral-900">{formatCurrency(r.total_outstanding)}</span> },
                        ]}
                        rows={arrears}
                        rowKey={r => `${r.tenant_id}-${r.property_id}`}
                        pageSize={10}
                        emptyMessage="No arrears."
                    />
                </Card>
            )}

            <Card padding={false}>
                <div className="p-4 pb-0">
                    <Toolbar search={search} onSearch={setSearch} placeholder="Search by invoice #, tenant, property…" />
                </div>
                {loading ? (
                    <div className="p-8 text-neutral-400 text-sm text-center">Loading…</div>
                ) : filtered.length === 0 && !search ? (
                    <div className="p-8">
                        <EmptyState icon={Receipt}
                            title="No invoices yet"
                            description="Generate this month's rent invoices, or wait for the daily 06:00 cron."
                            action={canGenerate && <button onClick={generate} className="btn-primary">
                                <Play className="h-4 w-4" /> Generate now
                            </button>} />
                    </div>
                ) : (
                    <DataTable
                        density="compact"
                        onRowClick={r => setDetail(r.id)}
                        columns={[
                            { key: 'invoice_number', label: 'Invoice #', mono: true, sortable: true },
                            { key: 'tenant_name',    label: 'Tenant', sortable: true },
                            { key: 'property_name',  label: 'Property', sortable: true },
                            { key: 'due_date',       label: 'Due', sortable: true,
                              render: r => new Date(r.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) },
                            { key: 'total_amount',   label: 'Total', align: 'right', sortable: true, render: r => formatCurrency(r.total_amount) },
                            { key: 'paid_amount',    label: 'Paid',  align: 'right', sortable: true, render: r => formatCurrency(r.paid_amount) },
                            { key: 'balance_due',    label: 'Balance', align: 'right', sortable: true,
                              render: r => <span className="font-semibold">{formatCurrency(r.balance_due)}</span> },
                            { key: 'status',         label: 'Status',
                              render: r => <Badge tone={statusTone(r.status)} dot>{r.status}</Badge> },
                        ]}
                        rows={filtered}
                        rowKey={r => r.id}
                        pageSize={25}
                        emptyMessage="No invoices match your search."
                    />
                )}
            </Card>

            {detail && <InvoiceDetail id={detail} onClose={() => setDetail(null)} />}
        </div>
    );
};

const InvoiceDetail = ({ id, onClose }) => {
    const [data, setData] = useState(null);
    useEffect(() => { axios.get(`/api/invoices/${id}`).then(r => setData(r.data)).catch(() => toast.error('Failed')); }, [id]);
    if (!data) return null;
    const inv = data.invoice;
    return (
        <div className="fixed inset-0 z-50 bg-neutral-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-lg animate-slide-up" onClick={e => e.stopPropagation()}>
                <div className="px-5 py-4 border-b border-neutral-100 flex justify-between items-center">
                    <div>
                        <h2 className="text-sm font-semibold text-neutral-900">Invoice {inv.invoice_number}</h2>
                        <p className="text-xs text-neutral-500">{inv.property_name} · {inv.tenant_name}</p>
                    </div>
                    <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700">✕</button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-4 gap-3 text-sm">
                        <div><div className="text-xs text-neutral-500">Due</div><div className="font-medium">{new Date(inv.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div></div>
                        <div><div className="text-xs text-neutral-500">Status</div><Badge tone={statusTone(inv.status)} dot>{inv.status}</Badge></div>
                        <div><div className="text-xs text-neutral-500">Total</div><div className="font-semibold tabular-nums">{formatCurrency(inv.total_amount)}</div></div>
                        <div><div className="text-xs text-neutral-500">Balance</div><div className="font-semibold tabular-nums text-warning-700">{formatCurrency(inv.balance_due)}</div></div>
                    </div>
                    <Card title="Payments applied" padding={false}>
                        {data.allocations.length === 0
                          ? <div className="p-6 text-sm text-neutral-400 text-center">No payments allocated yet.</div>
                          : <DataTable
                                density="compact"
                                columns={[
                                    { key: 'payment_date', label: 'Date', render: r => new Date(r.payment_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) },
                                    { key: 'payment_method', label: 'Method', render: r => <span className="capitalize">{r.payment_method}</span> },
                                    { key: 'reference_number', label: 'Ref', mono: true },
                                    { key: 'receipt_number',   label: 'Receipt', mono: true },
                                    { key: 'amount_applied',   label: 'Amount', align: 'right', render: r => formatCurrency(r.amount_applied) },
                                ]}
                                rows={data.allocations}
                                rowKey={(r, i) => i}
                                pageSize={10}
                            />}
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Invoices;
